import { useState } from 'react';
import { useIsMobile } from '@/shared/lib';

/** The two ways a table can present its rows. */
export type Presentation = 'table' | 'cards';

/**
 * The presentation on screen, and the setter the view toggle calls.
 *
 * ON MOBILE THE PRESENTATION IS CARDS, AND IT IS NOT A CHOICE.
 * A table on a phone is not a smaller table — it is a table whose columns run
 * off the screen, so the user has to pan sideways to read a row and loses the
 * row's identity while doing it. Cards stack vertically and show one record at
 * a time, which is what a narrow viewport can actually present. So the default
 * is cards below the `sm` breakpoint, and the view toggle is hidden there
 * (see `viewToggleGroup`) because there is no second presentation to switch
 * to.
 *
 * THE DEFAULT FOLLOWS THE BREAKPOINT, NOT JUST THE FIRST RENDER.
 * A `useState` initializer runs once, so a console opened on a desktop and
 * then narrowed — or a phone rotated from portrait to landscape and back —
 * would keep whichever presentation it started with. A native app re-lays out
 * when the device does, so the internal presentation is re-derived whenever
 * the breakpoint is crossed: into mobile selects cards, back selects table.
 *
 * The reset happens DURING RENDER, not in an effect. React's documented
 * pattern for "state that must follow a changing input" is to compare against
 * the previous input and set state in the same render, which re-runs the
 * render immediately with the new value and never paints the stale
 * presentation. An effect would paint the old presentation for a frame first,
 * and the lint rule forbids the direct `setState` call anyway.
 *
 * A caller that passes `presentation` keeps full control and is never
 * overridden, so the reset is skipped when the table is controlled.
 */
export function usePresentation(controlled: Presentation | undefined): {
  presentation: Presentation;
  setPresentation: (next: Presentation) => void;
} {
  const isMobile = useIsMobile();
  const [internal, setInternal] = useState<Presentation>(isMobile ? 'cards' : 'table');

  const [lastIsMobile, setLastIsMobile] = useState(isMobile);
  if (lastIsMobile !== isMobile) {
    setLastIsMobile(isMobile);
    if (controlled === undefined) setInternal(isMobile ? 'cards' : 'table');
  }

  return { presentation: controlled ?? internal, setPresentation: setInternal };
}
